import { useMemo } from "react";
import {
  Button,
  Input,
  SelectSearch,
  Form,
  ModalFooter,
  DatePicker,
  InputMultipleBadge,
} from "@telluscompany/design-system";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocalization } from "@i18n";
import { createLicenseSchema, type LicenseFormData } from "../../schemas";
import { mapReleasedModulesToSelectOptions } from "../../utils/moduleSelectOptions";
import { useListStructuresQuery } from "../../../structures/api/structures/structures.queries";
import type { ListStructuresResponse } from "../../../structures/api/structures/structures.types";
import { useListModulesQuery } from "../../api/licenses/licenses.queries";

interface CreateLicenseFormProps {
  tenantId: string;
  onSubmit: (data: LicenseFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const CreateLicenseForm = ({
  tenantId,
  onSubmit,
  onCancel,
  isSubmitting,
}: CreateLicenseFormProps) => {
  const { translate } = useLocalization();

  const { data: structuresData, isLoading: isLoadingStructures } =
    useListStructuresQuery(tenantId, { limit: 100 });

  const { data: modulesData, isLoading: isLoadingModules } =
    useListModulesQuery(tenantId);

  const structureOptions = useMemo(
    () =>
      structuresData?.data?.map((s: ListStructuresResponse) => ({
        label: `${s.name} (${s.complexName})`,
        value: s.id,
      })) ?? [],
    [structuresData],
  );

  const moduleOptions = useMemo(
    () => mapReleasedModulesToSelectOptions(modulesData, translate),
    [modulesData, translate],
  );

  const form = useForm<LicenseFormData>({
    resolver: zodResolver(createLicenseSchema(translate)),
    defaultValues: {
      structureId: "",
      moduleId: "",
      quantity: 0,
      managementEmail: "",
      clientEmail: "",
      notificationDays: [],
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="px-[32px] py-[16px] max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-x-4">
            {/* Seção 1: Estrutura */}
            <SelectSearch
              control={form.control}
              label={translate("management.licenses.form.label.structure")}
              name="structureId"
              placeholder={translate(
                "management.licenses.form.placeholder.structure",
              )}
              options={structureOptions}
              disabled={isLoadingStructures}
            />

            {/* Seção 2: Módulo / Licenças */}
            <SelectSearch
              control={form.control}
              label={translate("management.licenses.form.label.module")}
              name="moduleId"
              placeholder={translate(
                "management.licenses.form.placeholder.module",
              )}
              options={moduleOptions}
              disabled={isLoadingModules}
            />
            <Input
              control={form.control}
              label={translate("management.licenses.form.label.licenses")}
              name="quantity"
              type="number"
              placeholder={translate(
                "management.licenses.form.placeholder.licenses",
              )}
            />

            {/* Seção 3: Período de acesso */}
            <DatePicker
              control={form.control}
              dateFormat="shortDateNumeric"
              label={translate("management.licenses.form.label.startDate")}
              placeholderText={translate(
                "management.licenses.form.label.startDate",
              )}
              name="startDate"
            />
            <DatePicker
              control={form.control}
              dateFormat="shortDateNumeric"
              label={translate("management.licenses.form.label.endDate")}
              placeholderText={translate(
                "management.licenses.form.label.endDate",
              )}
              name="endDate"
            />

            {/* Seção 4: Emails */}
            <Input
              control={form.control}
              label={translate("management.licenses.form.label.managerEmail")}
              name="managementEmail"
              type="email"
              placeholder={translate(
                "management.licenses.form.placeholder.managerEmail",
              )}
            />
            <Input
              control={form.control}
              label={translate("management.licenses.form.label.clientEmail")}
              name="clientEmail"
              type="email"
              placeholder={translate(
                "management.licenses.form.placeholder.clientEmail",
              )}
            />

            {/* Seção 5: Período de notificação */}
            <div className="col-span-2">
              <InputMultipleBadge
                control={form.control}
                label={translate(
                  "management.licenses.form.label.notificationPeriods",
                )}
                name="notificationDays"
                placeholder={translate(
                  "management.licenses.form.placeholder.notificationPeriods",
                )}
                unit={translate("management.licenses.form.label.days")}
                maxBadges={3}
                badgeAlreadyExistsMessage={translate(
                  "management.licenses.validation.notificationPeriodAlreadyExists",
                )}
                maxBadgesReachedMessage={translate(
                  "management.licenses.validation.notificationPeriodsMax",
                )}
              />
            </div>
          </div>
        </div>
        <ModalFooter
          footer={
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {translate("management.licenses.button.save")}
            </Button>
          }
          closeModal={
            <Button type="button" variant="secondary" onClick={onCancel}>
              {translate("management.licenses.button.cancel")}
            </Button>
          }
        />
      </form>
    </Form>
  );
};
